import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { useMarketPricesUi } from '@/hooks/useMarketPricesUi';
import { loadRange, useLineColors, f0, f2, pct, dmy, dev, DASH, type RangeResp, type LineKey, type Totals } from './pricesData';

/**
 * РДН «За місяць» — один запит на місяць (/market/price-forecast/range?month=), без 31×3 запитів по добах.
 * Графік — індекс BASE кожної доби: факт, лінії BASE / EXPRO і «як учора»; майбутні доби — лише прогноз.
 * Плитки — середні за доби з фактом; прогноз порівнюємо лише на тих самих добах (де є і факт, і прогноз).
 */

type Vis = { fact: boolean; base: boolean; expro: boolean; naive: boolean };
const avg = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null);

export default function RdnPricesMonth({ date, view, controls }: { date: string; view: 'chart' | 'table'; controls: ReactNode }) {
  const { t } = useTranslation();
  const c = useLineColors();
  const { setRange } = useMarketPricesUi();
  const ym = (date || '').slice(0, 7);
  const [r, setR] = useState<RangeResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [vis, setVis] = useState<Vis>({ fact: true, base: true, expro: true, naive: false });

  useEffect(() => {
    if (!ym) return;
    let dead = false;
    setLoading(true);
    loadRange({ month: ym }).then((x) => { if (!dead) setR(x); }).catch(() => { if (!dead) setR(null); })
      .finally(() => { if (!dead) setLoading(false); });
    return () => { dead = true; };
  }, [ym]);
  useEffect(() => { /* межі навігації ставить вигляд доби (dam-summary); тут лише не скидаємо їх */ }, [setRange]);

  const days = r?.days ?? [];
  const has = { base: days.some((d) => d.base), expro: days.some((d) => d.expro), naive: days.some((d) => d.hasFact && d.naive) };

  // Плитки: факт — середнє індексів діб із фактом; лінія — середнє на тих самих добах, де є і факт, і ця лінія.
  const tiles = useMemo(() => {
    const fd = days.filter((d) => d.actual);
    const pick = (k: 'base' | 'peak' | 'offpeak', line: LineKey) => {
      const both = fd.filter((d) => d[line]);
      return { fact: avg(both.map((d) => d.actual![k] as number).filter((x) => x != null)), fc: avg(both.map((d) => d[line]![k] as number).filter((x) => x != null)), n: both.length };
    };
    const fact = (k: 'base' | 'peak' | 'offpeak') => avg(fd.map((d) => d.actual![k] as number).filter((x) => x != null));
    const wv = fd.filter((d) => d.weighted != null && d.volume);
    const weighted = wv.length ? wv.reduce((s, d) => s + d.weighted! * d.volume!, 0) / wv.reduce((s, d) => s + d.volume!, 0) : null;
    return {
      n: fd.length,
      base: { fact: fact('base'), b: pick('base', 'base'), e: pick('base', 'expro') },
      peak: { fact: fact('peak'), b: pick('peak', 'base'), e: pick('peak', 'expro') },
      offpeak: { fact: fact('offpeak'), b: pick('offpeak', 'base'), e: pick('offpeak', 'expro') },
      weighted,
    };
  }, [days]);

  const rows = useMemo(() => days.map((d) => ({
    dd: d.date.slice(8, 10), date: d.date, fact: d.actual?.base ?? null, base: d.base?.base ?? null, expro: d.expro?.base ?? null,
    // базис (ціна попередньої доби) — лише на добах з фактом
    naive: d.hasFact ? d.naive?.base ?? null : null, volume: d.volume, accB: d.metrics.base ? `${d.metrics.base.accurate}/${d.metrics.base.total}` : '—',
    accN: d.metrics.naive ? `${d.metrics.naive.accurate}/${d.metrics.naive.total}` : '—',
  })), [days]);

  const [lo, hi] = useMemo(() => {
    const vals = rows.flatMap((x) => [vis.fact ? x.fact : null, vis.base ? x.base : null, vis.expro ? x.expro : null, vis.naive ? x.naive : null])
      .filter((v): v is number => v != null);
    if (!vals.length) return [0, 10000];
    return [Math.max(0, Math.floor((Math.min(...vals) - 300) / 1000) * 1000), Math.ceil((Math.max(...vals) + 300) / 1000) * 1000];
  }, [rows, vis]);

  const Tile = ({ title, fact, b, e }: { title: string; fact: number | null; b?: { fc: number | null; fact: number | null; n: number }; e?: { fc: number | null; n: number } }) => (
    <div className="tile p-3.5 flex flex-col" style={{ minHeight: 132 }}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-semibold tracking-wide uppercase truncate" style={{ color: 'var(--text-muted)' }}>{title}</div>
        {fact != null && <span className="text-[9px] font-bold tracking-wider px-1.5 py-0.5 rounded-md shrink-0"
          style={{ background: 'var(--surface-2)', border: '1px solid var(--line)', color: 'var(--text-muted)' }}>{t('pv2_badge_fact')}</span>}
      </div>
      <div className="flex items-baseline gap-2 mt-1.5" style={{ minHeight: 30 }}>
        {fact != null
          ? <span className="font-display text-[24px] leading-none font-semibold" style={{ color: c.fact, fontVariantNumeric: 'tabular-nums' }}>{f2(fact)}</span>
          : <span className="block rounded" style={{ width: 22, height: 3, background: 'var(--line-strong)', alignSelf: 'center' }} />}
        <span className="text-[10px]" style={{ color: 'var(--text-dim)' }}>{t('pv2_unit')}</span>
      </div>
      {b && (
        <div className="mt-auto pt-2 flex flex-col gap-0.5 text-[12px]">
          <div className="flex items-baseline justify-between">
            {/* прогноз — середнє лише за доби, де є і факт, і прогноз: показуємо, за скільки діб */}
            <span style={{ color: 'var(--text-muted)' }}>BASE{b.fc != null && b.n < tiles.n ? <span style={{ color: 'var(--text-dim)' }}> · {t('pv2_n_days', { n: b.n })}</span> : null}</span>
            <span className="font-display text-[16px] font-semibold" style={{ color: b.fc == null ? 'var(--text-dim)' : c.base }}>{f2(b.fc)}</span>
          </div>
          {e && e.fc != null && (
            <div className="flex items-baseline justify-between">
              <span style={{ color: 'var(--text-muted)' }}>EXPRO</span>
              <span className="font-display text-[16px] font-semibold" style={{ color: c.expro }}>{f2(e.fc)}</span>
            </div>
          )}
          <div className="flex items-baseline justify-between">
            <span style={{ color: 'var(--text-muted)' }}>{t('pv2_deviation')}</span>
            <span className="font-semibold" style={{ color: 'var(--text)' }}>{pct(dev(b.fact, b.fc), 2)}</span>
          </div>
        </div>
      )}
    </div>
  );

  const T = r?.totals;
  // Рядок якості: значення лінії, а в дужках — «як учора» на ТИХ САМИХ добах.
  const qRow = (k: LineKey, x: Totals | null | undefined) => {
    if (!x) return null;
    const n = x.naive;
    const td = (v: ReactNode) => <td className="py-2 px-2 text-right tabular-nums" style={{ borderBottom: '1px solid var(--line)' }}>{v}</td>;
    const vs = (a: string, b: string | null) => <>{a}{b != null && <span style={{ color: 'var(--text-dim)' }}> ({b})</span>}</>;
    const mln = (v: number) => f2(v / 1e6);
    return (
      <tr key={k}>
        <td className="py-2 px-2 font-semibold" style={{ color: c[k], borderBottom: '1px solid var(--line)' }}>{k.toUpperCase()}</td>
        {td(x.days)}
        {td(vs(`${f0(x.mae)} ${t('pv2_uah')}`, n ? f0(n.mae) : null))}
        {td(vs(`${Math.round(x.accurate / x.hours * 100)} %`, n ? `${Math.round(n.accurate / n.hours * 100)} %` : null))}
        {td(x.inCorridor != null ? `${Math.round(x.inCorridor / x.hours * 100)} % · ${f0(x.corridorWidth)} ${t('pv2_uah')}` : '—')}
        {td(x.bess && x.bess.ideal ? vs(`${mln(x.bess.realized)} ${t('pv2_m_uah')} · ${Math.round(x.bess.realized / x.bess.ideal * 100)} %`,
          n?.bessRealized != null ? `${Math.round(n.bessRealized / x.bess.ideal * 100)} %` : null) : '—')}
      </tr>
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" style={{ opacity: loading && !r ? 0.5 : 1, transition: 'opacity .15s' }}>
        <Tile title="BASE" fact={tiles.base.fact} b={tiles.base.b} e={tiles.base.e} />
        <Tile title={t('pv2_weighted')} fact={tiles.weighted} />
        <Tile title="PEAK" fact={tiles.peak.fact} b={tiles.peak.b} e={tiles.peak.e} />
        <Tile title="OFFPEAK" fact={tiles.offpeak.fact} b={tiles.offpeak.b} e={tiles.offpeak.e} />
        <div className="tile p-3.5 flex flex-col" style={{ minHeight: 132 }}>
          <div className="text-[11px] font-semibold tracking-wide uppercase" style={{ color: 'var(--text-muted)' }}>{t('pv2_accuracy')}</div>
          <div className="flex items-baseline gap-1.5 mt-1.5" style={{ minHeight: 30 }}>
            {T?.base
              ? <><span className="font-display text-[24px] leading-none font-semibold" style={{ color: 'var(--text)' }}>{Math.round(T.base.accurate / T.base.hours * 100)} %</span>
                  <span className="text-[12px]" style={{ color: 'var(--text-muted)' }}>{t('pv2_accurate_hours')}</span></>
              : <span className="block rounded" style={{ width: 22, height: 3, background: 'var(--line-strong)', alignSelf: 'center' }} />}
          </div>
          <div className="mt-auto pt-2 flex flex-col gap-0.5 text-[12px]">
            <div className="flex items-baseline justify-between">
              <span style={{ color: 'var(--text-muted)' }}>{t('pv2_naive')}</span>
              <span className="font-semibold">{T?.base?.naive ? `${Math.round(T.base.naive.accurate / T.base.naive.hours * 100)} %` : '—'}</span>
            </div>
            <div className="text-[10.5px] leading-snug" style={{ color: 'var(--text-dim)' }}>{T?.base ? t('pv2_month_days', { n: T.base.days }) : t('pv2_after_fact')}</div>
          </div>
        </div>
      </div>

      <div className="flex justify-end">{controls}</div>

      <div className="tile p-4 sm:p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="font-display text-[15px] font-semibold" style={{ color: 'var(--text)' }}>{t('pv2_month_title')}</div>
            <div className="text-[12px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{t('pv2_month_sub')}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {([['fact', t('pv2_line_fact'), c.fact, undefined, true], ['base', 'BASE', c.base, undefined, has.base], ['expro', 'EXPRO', c.expro, DASH.expro, has.expro],
               ['naive', t('pv2_line_naive'), c.naive, DASH.naive, has.naive]] as [keyof Vis, string, string, string | undefined, boolean][]).map(([k, label, color, dash, ok]) => (
              <button key={k} type="button" disabled={!ok} onClick={() => setVis((v) => ({ ...v, [k]: !v[k] }))}
                className="flex items-center justify-center gap-1.5 rounded-md text-xs font-medium transition-all whitespace-nowrap"
                style={{ minWidth: 110, padding: '6px 12px', background: color + '1F', color: 'var(--text)', border: '1px solid ' + color + '66',
                         opacity: !ok ? 0.35 : vis[k] ? 1 : 0.45, cursor: ok ? 'pointer' : 'not-allowed' }}>
                <svg width="18" height="6" aria-hidden><line x1="1" y1="3" x2="17" y2="3" stroke={color} strokeWidth="2.5" strokeDasharray={dash} strokeLinecap="round" /></svg>
                {label}
              </button>
            ))}
          </div>
        </div>
        {loading && !r ? (
          <div className="flex items-center justify-center text-xs" style={{ color: 'var(--text-muted)', height: 340 }}>{t('pv2_loading')}</div>
        ) : view === 'chart' ? (
          <div style={{ height: 340 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={rows} margin={{ top: 10, right: 8, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
                <XAxis dataKey="dd" tick={{ fontSize: 10, fill: c.dim }} axisLine={false} tickLine={false} interval={0} />
                <YAxis domain={[lo, hi]} width={56} tick={{ fontSize: 10, fill: c.dim }} axisLine={false} tickLine={false} tickFormatter={(v: number) => f0(v)} />
                <Tooltip contentStyle={{ background: c.surface, border: `1px solid ${c.grid}`, borderRadius: 10, fontSize: 12, color: c.text }}
                  labelFormatter={(_l: string, p: any[]) => (p?.[0]?.payload ? dmy(p[0].payload.date) : '')}
                  formatter={(v: number | null, n: string) => [v == null ? '—' : `${f0(v)} ${t('pv2_unit')}`,
                    n === 'fact' ? t('pv2_line_fact') : n === 'naive' ? t('pv2_line_naive') : n.toUpperCase()]} />
                {vis.naive && <Line dataKey="naive" name="naive" stroke={c.naive} strokeWidth={1.5} strokeDasharray={DASH.naive} dot={false} connectNulls isAnimationActive={false} />}
                {vis.base && <Line dataKey="base" name="base" stroke={c.base} strokeWidth={2.25} dot={{ r: 2 }} connectNulls />}
                {vis.expro && <Line dataKey="expro" name="expro" stroke={c.expro} strokeWidth={2.5} strokeDasharray={DASH.expro} dot={{ r: 2, fill: c.expro }} connectNulls />}
                {vis.fact && <Line dataKey="fact" name="fact" stroke={c.fact} strokeWidth={2.5} dot={{ r: 2 }} connectNulls />}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="overflow-x-auto" style={{ border: '1px solid var(--line)', borderRadius: 12 }}>
            <table style={{ borderCollapse: 'collapse', minWidth: 1100 }}>
              <tbody>
                {([
                  [t('pv2_day'), rows.map((x) => x.dd), 'head', ''],
                  [t('pv2_row_fact_base'), rows.map((x) => f0(x.fact)), 'strong', c.fact],
                  ...(has.base ? [[t('pv2_row_base_base'), rows.map((x) => f0(x.base)), 'strong', c.base],
                    [t('pv2_row_dev_base'), rows.map((x) => pct(dev(x.fact, x.base), 1)), '', ''],
                    [t('pv2_row_acc_base'), rows.map((x) => x.accB), '', '']] : []),
                  ...(has.expro ? [[t('pv2_row_expro_base'), rows.map((x) => f0(x.expro)), 'strong', c.expro]] : []),
                  [t('pv2_row_naive_base'), rows.map((x) => f0(x.naive)), '', ''],
                  [t('pv2_row_acc_naive'), rows.map((x) => x.accN), '', ''],
                  [t('pv2_row_volume'), rows.map((x) => f0(x.volume)), '', ''],
                ] as [string, string[], string, string][]).map(([label, cells, kind, color]) => (
                  <tr key={label}>
                    <td style={{ padding: '8px 10px', fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', background: kind === 'head' ? 'var(--surface-2)' : 'var(--surface-solid)', position: 'sticky', left: 0, minWidth: 210, borderBottom: '1px solid var(--line)', borderRight: '1px solid var(--line)', whiteSpace: 'nowrap' }}>{label}</td>
                    {cells.map((cell, i) => (
                      <td key={i} style={{ padding: '8px 8px', fontSize: 12.5, textAlign: 'center', whiteSpace: 'nowrap', borderBottom: '1px solid var(--line)', borderRight: '1px solid var(--line)',
                        background: kind === 'head' ? 'var(--surface-2)' : undefined, fontWeight: kind ? 600 : 400, color: color || (kind === 'head' ? 'var(--text-muted)' : 'var(--text)') }}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {T && (T.base || T.expro) && (
        <div className="tile p-4 sm:p-5">
          <div className="font-display text-[15px] font-semibold" style={{ color: 'var(--text)' }}>{t('pv2_quality_month_title')}</div>
          <div className="text-[12px] mt-0.5 mb-3" style={{ color: 'var(--text-muted)' }}>{t('pv2_quality_month_sub')}</div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" style={{ borderCollapse: 'collapse', minWidth: 720 }}>
              <thead>
                <tr style={{ color: 'var(--text-muted)' }}>
                  {[t('pv2_q_line'), t('pv2_q_days'), t('pv2_q_mae'), t('pv2_q_accurate'), t('pv2_q_band_w'), t('pv2_q_bess')].map((h, i) => (
                    <th key={h} className="font-semibold py-2 px-2" style={{ textAlign: i ? 'right' : 'left', borderBottom: '1px solid var(--line)' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>{qRow('base', T.base)}{qRow('expro', T.expro)}</tbody>
            </table>
          </div>
          <div className="text-[11px] mt-2 leading-snug" style={{ color: 'var(--text-dim)' }}>{t('pv2_quality_month_note')}</div>
        </div>
      )}
    </div>
  );
}
